// ink-component
import React from 'react';
import styled from 'styled-components';

import getGlobalProps from '../../helpers/constants/global-props';
import useWindowWidth from '../../hooks/useWindowWidth';

import { elevationTokens } from '../../tokens/globals/tokens';
import { fullPageCelebrationTokens } from '../../tokens/components/FullPageCelebration/tokens';

import Box from '../Box/Box';
import Button from '../Button/Button';
import CustomText from '../Typography/CustomText/CustomText';
import Icon from '../Icon/Icon';
import VStack from '../VStack/VStack';
import HStack from '../HStack/HStack';

import Confetti from './components/ConfettiClass';
import ActionsBox from './components/ActionsBox';

const FullPageCelebrationWrapper = styled.div`
  background-color: transparent;
  bottom: 0;
  color: ${fullPageCelebrationTokens.CoreTextColor};
  display: flex;
  flex-direction: column;
  height: 100%;
  left: 0;
  opacity: 1;
  outline: 0;
  overflow: hidden auto;
  position: fixed;
  right: 0;
  top: 0;
  width: auto;
  z-index: ${elevationTokens.GlobalCelebration};
`;

type CanvasTypes = {
  transparent?: boolean;
  pointerEvents?: boolean;
  zIndex?: string;
  duration?: number;
  startFull?: boolean;
};

function FullPageCelebrationConfettiCanvas({
  transparent = false,
  pointerEvents = true,
  zIndex = elevationTokens.GlobalCelebration,
  startFull = true,
}: CanvasTypes) {
  // eslint-disable-next-line ssr-friendly/no-dom-globals-in-react-fc
  const ref = React.useRef<HTMLCanvasElement>(null);
  React.useEffect(() => {
    if (!ref.current) {
      return;
    }

    const confetti = new Confetti(ref.current, transparent, pointerEvents, startFull, zIndex);
    confetti.start();

    return () => confetti.kill();
  }, [pointerEvents, startFull, transparent, zIndex]);

  return <canvas ref={ref} />;
}

export interface ActionTypes {
  label?: string;
  callback?: (event?: any) => void;
  id?: string | undefined;
}
export interface PrimaryActionTypes extends ActionTypes {
  type?: 'primary' | 'default';
}
export interface FullPageCelebrationProps {
  /** The title for the celebration. Usually "Congratulations" */
  title: string;
  /** A description of what the celebration is for */
  text?: string;
  /** A ink button for a primary user action, can be primary or default. Format {label: string, callback: (event?: any) => void, id: string | number, type: 'primary' | 'default'}  */
  primaryAction: PrimaryActionTypes;
  /** A default ink button for a secondary user action. Format {label: string, callback: (event?: any) => void, id: string | number} */
  secondaryAction?: ActionTypes;
  /** A callback defining what should happen when the user clicks the closing button */
  onClickClose: () => void;
  /** Marks the element with a given ID to make it easier to select in tests */
  'data-testid'?: string | number;
}

/**
 * @purpose Bring joy to people's lives with a little magic
 * @categories accessory
 * @aka confetti
 */

export const FullPageCelebration = (props: FullPageCelebrationProps) => {
  const { title, text, onClickClose, 'data-testid': dataTestId } = props;
  const { isMobile } = useWindowWidth();

  const globalProps = getGlobalProps({});

  return (
    <div data-testid={dataTestId}>
      <FullPageCelebrationConfettiCanvas />
      <FullPageCelebrationWrapper {...globalProps}>
        <HStack align="right">
          <Box top="large" right="large">
            <CustomText>
              <Button type="aura" onClick={onClickClose}>
                <Icon type="close" size="xxl" meaning="close celebration modal" />
              </Button>
            </CustomText>
          </Box>
        </HStack>
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          alignSelf="center"
          justifyContent="center"
          flexGrow={1}
          maxWidth={['80%', '60%', '50%', '800px', '1000px']}
        >
          <VStack align="center" spacing="small">
            <CustomText as="h1" align="center" size={isMobile ? '36' : '48'}>
              {title}
            </CustomText>
            <CustomText align="center" size={isMobile ? 'body' : 'xlarge'}>
              {text}
            </CustomText>
            <ActionsBox {...props} />
          </VStack>
        </Box>
      </FullPageCelebrationWrapper>
    </div>
  );
};

FullPageCelebration.displayName = 'FullPageCelebration';

FullPageCelebration.defaultProps = {
  title: 'Congratulations!',
  text: undefined,
  primaryAction: {
    label: undefined,
    callback: () => {},
    id: undefined,
    type: 'primary',
  },
  secondaryAction: {
    label: undefined,
    callback: () => {},
    id: undefined,
  },
  onClickClose: undefined,
  'data-testid': undefined,
};

export default FullPageCelebration;
